/**
 * Levertijd die de configurator bij een spoedkolom toont.
 *
 * Volgorde:
 * 1. De metadata van de gekozen `urgency`-waarde (bereik en/of dagen).
 * 2. Zonder `urgency`-optie: een expliciete levertijd uit de specificaties
 *    (`deliveryDays`, `leadTimeBusinessDays`, `productionDays`).
 * 3. De vaste tabel per spoedniveau (standaard "4-6 werkdagen").
 *
 * Tot 19 okt 2026 won de vaste tabel van stap 2, waardoor een CUSTOM-product
 * zonder spoedoptie altijd "4-6 werkdagen" toonde, ook met
 * `specifications.deliveryDays: 10`. FORADAY kreeg daarom een verborgen
 * spoedoptie als omweg; die blijft gewoon werken via stap 1.
 */
export const URGENCY_LEAD_TIMES: Record<string, { label: string; days: number; range: string; description: string }> = {
  standard: { label: 'Standaard', days: 5, range: '4-6 werkdagen', description: 'Normale productie' },
  moderate: { label: 'Dagje eerder', days: 4, range: '3-5 werkdagen', description: 'Een dag eerder dan standaard' },
  quick: { label: 'Snel', days: 3, range: '2-4 werkdagen', description: 'Snellere productie' },
  urgent: { label: 'Spoed', days: 2, range: '1-3 werkdagen', description: 'Spoedproductie' },
  fast: { label: 'Snel', days: 3, range: '2-4 werkdagen', description: 'Snellere productie' },
  faster: { label: 'Spoed', days: 2, range: '1-3 werkdagen', description: 'Spoedproductie' },
  express: { label: 'Express', days: 1, range: '1-2 werkdagen', description: 'Snelste productie' },
};

/** Expliciet in de productspecificaties vastgelegde levertijd in werkdagen. */
export function explicitSpecificationLeadTime(specifications: unknown): number | undefined {
  const specs = (specifications && typeof specifications === 'object' ? specifications : {}) as Record<string, unknown>;
  const days = Number(specs.deliveryDays ?? specs.leadTimeBusinessDays ?? specs.productionDays);
  return Number.isFinite(days) && days > 0 ? days : undefined;
}

export function resolveUrgencyLeadTime(input: {
  urgencyId: string;
  /** Metadata van de gekozen urgency-waarde, als die bestaat. */
  metadata?: Record<string, unknown> | null;
  hasUrgencyOption: boolean;
  /** Uit explicitSpecificationLeadTime(). */
  explicitDays?: number;
  /** Leverancierswaarde als laatste terugval voor het aantal dagen. */
  fallbackDays: number;
}): { range: string; days: number } {
  const metadata = input.metadata || {};
  const table = URGENCY_LEAD_TIMES[input.urgencyId] || URGENCY_LEAD_TIMES.standard;
  const metadataRange = metadata.businessDaysRange || metadata.businessDaysText;
  const metadataDays = metadata.deliveryDays ?? metadata.leadTimeBusinessDays ?? metadata.productionDays;

  // Lege range: de aanroeper toont "<dagen> werkdagen".
  if (!metadataRange && metadataDays == null && !input.hasUrgencyOption && input.explicitDays) {
    return { range: '', days: input.explicitDays };
  }

  const days = Number(metadataDays ?? table.days ?? input.fallbackDays);
  return {
    range: String(metadataRange || table.range),
    days: Number.isFinite(days) && days > 0 ? days : input.fallbackDays,
  };
}
